import React from "react";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import Player from "core/components/Player";
import Skeleton from "@mui/material/Skeleton";
import { useViewer } from "core/components/ViewerProvider";
import ProductDialog from "arena/components/ProductDialog";
import useProductDialog from "arena/hooks/useProductDialog";
import { usePlayerFullscreen } from "core/components/PlayerFullscreenProvider";

const Root = styled("div", {
  shouldForwardProp: (prop) => prop !== "isPlayerFullscreen",
})(({ theme, isPlayerFullscreen }) => ({
  zIndex: isPlayerFullscreen ? 9999 : 1,
  position: isPlayerFullscreen ? "fixed" : "relative",
  top: isPlayerFullscreen ? 0 : "auto",
  left: isPlayerFullscreen ? 0 : "auto",
  width: isPlayerFullscreen ? "100vw" : "auto",
  height: isPlayerFullscreen ? "100vh" : 0,
  paddingTop: isPlayerFullscreen ? 0 : "56.25%",
  backgroundColor: theme.palette.mode === "dark" ? "black" : "rgba(0,0,0,0.07)",

  "&:fullscreen": {
    paddingTop: 0,
  },
}));

const StyledSkeleton = styled(Skeleton)(({ theme }) => ({
  width: "100%",
  height: 0,
  paddingTop: "56.25%",
  backgroundColor: theme.palette.mode === "dark" ? "black" : "rgba(0,0,0,0.07)",
  "&:fullscreen": {
    paddingTop: 0,
  },
}));

export default function ContentPlayer({
  closeRouteDialogButton,
  nextPlaylistContentButton,
  onChatAdv,
  muted,
  autoPlay,
  hideTitle,
  content,
  subscribeButton,
  onEnded,
  start,
  finish,
  sponsorCornerBannerPlace,
  sponsorInStreamBannerPlace,
  sponsorPlayerLogoPlace,
  sponsorPlayerButtonPlace,
  sponsorVideoPlace,
  sponsorChatMessagePlace,
  onSeeked,
  onTimeUpdate,
  onSeekEnd,
  onUpdate,
  refetch,
  startOffset,
  suspended,
  onPlayIntent,
  prePlayLayout,
  // chatEnabled,
}) {
  const viewer = useViewer();
  const { state, dispatch } = useProductDialog();
  const { isPlayerFullscreen } = usePlayerFullscreen();

  if (!content) {
    return <StyledSkeleton variant="rectangular" />;
  }

  return (
    <Root id="player" isPlayerFullscreen={isPlayerFullscreen}>
      <Player
        key={content.id}
        startOffset={startOffset}
        suspended={suspended}
        nextPlaylistContentButton={nextPlaylistContentButton}
        onChatAdv={onChatAdv}
        onEnded={onEnded}
        autoPlay={autoPlay}
        onPlayIntent={onPlayIntent}
        prePlayLayout={prePlayLayout}
        content={content}
        hideTitle={hideTitle}
        onSeeked={onSeeked}
        onTimeUpdate={onTimeUpdate}
        onUpdate={onUpdate}
        onSeekEnd={onSeekEnd}
        hideRelatedContent
        hideShare={content.hideShare}
        hideLogo
        muted={muted}
        fullscreenTarget="player"
        updateVideoPosition={!!viewer}
        subscribeButton={subscribeButton}
        start={start}
        finish={finish}
        sponsorCornerBannerPlace={sponsorCornerBannerPlace}
        sponsorInStreamBannerPlace={sponsorInStreamBannerPlace}
        sponsorPlayerLogoPlace={sponsorPlayerLogoPlace}
        sponsorPlayerButtonPlace={sponsorPlayerButtonPlace}
        sponsorVideoPlace={sponsorVideoPlace}
        sponsorChatMessagePlace={sponsorChatMessagePlace}
        onClickProductButton={dispatch}
        closeRouteDialogButton={closeRouteDialogButton}
      />

      <ProductDialog
        referralContentId={content.id}
        products={content.products.nodes}
        state={state}
        dispatch={dispatch}
        refetch={refetch}
      />
    </Root>
  );
}

ContentPlayer.propTypes = {
  suspended: PropTypes.bool,
  startOffset: PropTypes.number,
  nextPlaylistContentButton: PropTypes.func,
  onChatAdv: PropTypes.func,
  muted: PropTypes.bool,
  autoPlay: PropTypes.bool,
  onPlayIntent: PropTypes.func,
  prePlayLayout: PropTypes.oneOf(["default", "embed"]),
  hideTitle: PropTypes.bool,
  onEnded: PropTypes.func,
  subscribeButton: PropTypes.node,
  content: PropTypes.shape({
    id: PropTypes.string.isRequired,
    hideShare: PropTypes.bool,
    premium: PropTypes.bool,
    viewerPurchase: PropTypes.object,
    title: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    products: PropTypes.shape({
      nodes: PropTypes.array.isRequired,
    }).isRequired,
    sport: PropTypes.shape({
      id: PropTypes.string.isRequired,
      slug: PropTypes.string,
    }),
  }),

  start: PropTypes.number,
  finish: PropTypes.number,
  sponsorCornerBannerPlace: PropTypes.string,
  sponsorInStreamBannerPlace: PropTypes.string,
  sponsorPlayerLogoPlace: PropTypes.string,
  sponsorPlayerButtonPlace: PropTypes.string,
  sponsorVideoPlace: PropTypes.string,
  sponsorChatMessagePlace: PropTypes.string,
  onSeeked: PropTypes.func,
  onTimeUpdate: PropTypes.func,
  onUpdate: PropTypes.func,
  onSeekEnd: PropTypes.func,
  refetch: PropTypes.func,
  chatEnabled: PropTypes.bool,
  closeRouteDialogButton: PropTypes.node,
};
